import { useEffect, useMemo, useState } from 'react';
import { diffWordsWithSpace } from 'diff';
import api from '../../lib/api';
import { cn } from '../../lib/utils';
import type { ScriptSnapshotMeta } from './scriptWorkspaceTypes';

interface VersionTabItem {
  version: number;
  label: string;
  authorLabel?: string;
  createdAt?: string;
  isLive: boolean;
}

interface Props {
  scriptId: string;
  /** Live working copy version + content (after the latest save). */
  liveVersion: number;
  liveContent: string;
  /** Older immutable snapshots, ordered by version ascending. */
  snapshots: ScriptSnapshotMeta[];
}

function formatRelative(iso: string | undefined): string {
  if (!iso) return '';
  try {
    const date = new Date(iso);
    const diff = Date.now() - date.getTime();
    const min = Math.round(diff / 60_000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.round(hr / 24);
    if (day < 7) return `${day}d ago`;
    return date.toLocaleDateString();
  } catch {
    return '';
  }
}

export default function StudioVersionDiff({ scriptId, liveVersion, liveContent, snapshots }: Props) {
  // Build the ordered list of version tabs: every snapshot + the live row.
  const tabs: VersionTabItem[] = useMemo(() => {
    const snapTabs: VersionTabItem[] = snapshots
      .filter((s) => s.version < liveVersion)
      .map((s) => ({
        version: s.version,
        label: `v${s.version}`,
        authorLabel: s.author?.fullName || (s.authorId ? 'Unknown' : 'System'),
        createdAt: s.createdAt,
        isLive: false,
      }));
    const liveTab: VersionTabItem = {
      version: liveVersion,
      label: `v${liveVersion}`,
      authorLabel: 'Working copy',
      isLive: true,
    };
    return [...snapTabs, liveTab];
  }, [snapshots, liveVersion]);

  const [selectedVersion, setSelectedVersion] = useState<number>(liveVersion);
  useEffect(() => {
    setSelectedVersion(liveVersion);
  }, [liveVersion, scriptId]);

  // Cache fetched snapshot content so flipping between tabs is instant.
  const [snapshotContent, setSnapshotContent] = useState<Record<number, string>>({});
  const [loadingVersion, setLoadingVersion] = useState<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Resolve content for a given version: live row or fetched snapshot.
  const contentForVersion = (v: number): string | undefined => {
    if (v === liveVersion) return liveContent;
    return snapshotContent[v];
  };

  // Fetch missing snapshot content lazily as the user picks a tab.
  useEffect(() => {
    const previousVersion = selectedVersion - 1;
    const wanted = [selectedVersion, previousVersion].filter(
      (v) => v >= 1 && v < liveVersion && !(v in snapshotContent)
    );
    if (wanted.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        setLoadingVersion(wanted[0]);
        const results = await Promise.all(
          wanted.map((v) =>
            api
              .get<{ version: number; content: string }>(`/scripts/${scriptId}/snapshots/${v}`)
              .then((r) => ({ v, content: r.data.content ?? '' }))
              .catch(() => ({ v, content: '' }))
          )
        );
        if (cancelled) return;
        setSnapshotContent((prev) => {
          const next = { ...prev };
          for (const r of results) next[r.v] = r.content;
          return next;
        });
        setLoadError(null);
      } catch (e: unknown) {
        if (!cancelled) {
          setLoadError(e instanceof Error ? e.message : 'Failed to load snapshot');
        }
      } finally {
        if (!cancelled) setLoadingVersion(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedVersion, liveVersion, scriptId, snapshotContent]);

  const previousVersion = selectedVersion - 1;
  const currentText = contentForVersion(selectedVersion);
  const previousText = previousVersion >= 1 ? contentForVersion(previousVersion) : undefined;

  const diffNodes = useMemo(() => {
    if (currentText == null || previousText == null) return null;
    return diffWordsWithSpace(previousText, currentText);
  }, [currentText, previousText]);

  return (
    <div className="rounded-lg border border-[#d4d1ca] bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
      {/* Version tabs at the top — mirror the script_perplex.html style. */}
      <div className="-mx-1 mb-3 flex flex-wrap gap-1 border-b border-[#e8e6e1] pb-2 dark:border-gray-700">
        {tabs.map((tab) => {
          const active = tab.version === selectedVersion;
          return (
            <button
              key={tab.version}
              type="button"
              onClick={() => setSelectedVersion(tab.version)}
              className={cn(
                'rounded-md px-2.5 py-1 text-xs font-medium transition-all',
                active
                  ? 'bg-[#01696f] text-white shadow-sm dark:bg-teal-700'
                  : 'border border-[#d4d1ca] bg-white text-[#77756f] hover:border-[#a8a6a1] hover:text-[#22211d] dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-100',
              )}
              title={
                tab.authorLabel
                  ? `${tab.label} · ${tab.authorLabel}${tab.createdAt ? ` · ${formatRelative(tab.createdAt)}` : ''}`
                  : tab.label
              }
            >
              <span className="mr-1">{tab.label}</span>
              {tab.isLive && (
                <span className="rounded bg-emerald-200 px-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-900 dark:bg-emerald-900 dark:text-emerald-100">
                  live
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Header + legend mirrors script_perplex.html:640-650. */}
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-[#22211d] dark:text-gray-100">
          {previousVersion >= 1 ? (
            <>
              Diff: <span className="text-[#77756f] dark:text-gray-400">v{previousVersion}</span>
              <span className="mx-1 text-[#a8a6a1]">→</span>
              <span className="text-[#01696f] dark:text-teal-400">v{selectedVersion}</span>
            </>
          ) : (
            <>First version — no diff to compare against.</>
          )}
        </h3>
        {previousVersion >= 1 && (
          <div className="flex gap-3 text-[10px] font-semibold uppercase tracking-wide text-[#77756f] dark:text-gray-400">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-5 rounded bg-emerald-200 dark:bg-emerald-900" />
              Added in v{selectedVersion}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-5 rounded bg-red-200 dark:bg-red-900" />
              Removed from v{previousVersion}
            </span>
          </div>
        )}
      </div>

      {previousVersion < 1 ? (
        <div className="rounded-md border border-dashed border-[#d4d1ca] bg-[#fdfcf9] p-3 text-xs text-[#77756f] dark:border-gray-700 dark:bg-gray-950 dark:text-gray-400">
          v{selectedVersion} is the first saved version of this script. Edit and save to create v{selectedVersion + 1} and
          generate a side-by-side comparison here.
        </div>
      ) : currentText == null || previousText == null ? (
        <div className="flex items-center gap-2 rounded-md border border-[#d4d1ca] bg-[#fdfcf9] p-3 text-xs text-[#77756f] dark:border-gray-700 dark:bg-gray-950 dark:text-gray-400">
          {loadError ? (
            <span className="text-red-600 dark:text-red-400">{loadError}</span>
          ) : (
            <>Loading version {loadingVersion ?? '…'}…</>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-[#77756f] dark:text-gray-400">
              v{previousVersion} (previous)
            </p>
            <div className="max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-md border border-[#d4d1ca] bg-[#fdfcf9] p-3 text-sm leading-relaxed text-[#22211d] dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100">
              {diffNodes!.map((node, i) => {
                if (node.added) return null;
                if (node.removed) {
                  return (
                    <span
                      key={i}
                      className="rounded bg-red-100 px-0.5 text-red-900 line-through decoration-red-400 dark:bg-red-950/40 dark:text-red-200"
                    >
                      {node.value}
                    </span>
                  );
                }
                return <span key={i}>{node.value}</span>;
              })}
            </div>
          </div>
          <div>
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-[#77756f] dark:text-gray-400">
              v{selectedVersion} {selectedVersion === liveVersion ? '(live)' : ''}
            </p>
            <div className="max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-md border border-[#d4d1ca] bg-[#fdfcf9] p-3 text-sm leading-relaxed text-[#22211d] dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100">
              {diffNodes!.map((node, i) => {
                if (node.removed) return null;
                if (node.added) {
                  return (
                    <span
                      key={i}
                      className="rounded bg-emerald-100 px-0.5 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200"
                    >
                      {node.value}
                    </span>
                  );
                }
                return <span key={i}>{node.value}</span>;
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
